import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';

/**
 * The phone mockups are built from real markup rather than screenshots:
 *  - they stay crisp at any density and in dark mode
 *  - no external image request (the old one pulled a stock photo from Unsplash)
 *  - when the app's UI changes, this is editable rather than needing a re-shoot
 * Everything inside them is decorative, so the whole cluster is aria-hidden and
 * the workflow is also stated in text for assistive tech.
 */

const PhoneFrame = ({ children, className = '' }) => (
  <div className={`relative bg-gray-900 rounded-[2rem] p-1.5 shadow-2xl ${className}`}>
    {/* notch */}
    <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-14 h-3 bg-gray-900 rounded-full z-10" />
    <div className="rounded-[1.7rem] overflow-hidden bg-gray-50 w-full aspect-[9/19] relative">
      {children}
    </div>
  </div>
);

const StatusBar = ({ title }) => (
  <div className="bg-white px-3 pt-5 pb-2 border-b border-gray-100">
    <p className="text-[9px] font-bold text-gray-900 text-center">{title}</p>
  </div>
);

/* ── 0. Splash ────────────────────────────────────────────────── */
// Mirrors the real app's splash (rose gradient — see app_theme.dart), so the
// demo opens the way the installed app actually does.
const SplashScreen = () => (
  <div className="h-full w-full bg-gradient-to-br from-rose-400 via-rose-500 to-rose-600 flex flex-col items-center justify-center gap-2">
    <div className="w-12 h-12 rounded-2xl bg-white/95 flex items-center justify-center shadow-lg">
      <Icon name="Home" size={24} className="text-rose-500" />
    </div>
    <p className="text-[11px] font-extrabold text-white tracking-tight">
      toletfor<span className="text-rose-100">rent</span>
    </p>
    <p className="text-[6px] text-rose-100 tracking-[0.2em] uppercase">Find your perfect home</p>
  </div>
);

/* ── 1. Search & discover ─────────────────────────────────────── */
const SearchScreen = () => (
  <>
    <StatusBar title="Search" />
    <div className="p-2 space-y-2">
      <div className="flex items-center gap-1.5 bg-white rounded-full px-2 py-1.5 border border-gray-200">
        <Icon name="Search" size={9} className="text-gray-400" />
        <span className="text-[7px] text-gray-400">Lucknow, Uttar Pradesh</span>
      </div>
      <div className="flex gap-1">
        {['Flats', 'Rooms', 'PG'].map((t, i) => (
          <span
            key={t}
            className={`text-[6px] px-1.5 py-0.5 rounded-full ${
              i === 0 ? 'bg-rose-500 text-white' : 'bg-white text-gray-500 border border-gray-200'
            }`}
          >
            {t}
          </span>
        ))}
      </div>
      {[
        { price: '₹14,000', spec: '2 BHK · Furnished', area: 'Indira Nagar' },
        { price: '₹8,500', spec: '1 BHK · Semi', area: 'Gomti Nagar' },
      ].map((p) => (
        <div key={p.price} className="bg-white rounded-lg overflow-hidden border border-gray-100">
          <div className="h-10 bg-gradient-to-br from-rose-100 to-gray-100" />
          <div className="p-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[8px] font-bold text-gray-900">{p.price}</span>
              <span className="text-[6px] text-gray-400">/mo</span>
            </div>
            <p className="text-[6px] text-gray-500 mt-0.5">{p.spec}</p>
            <p className="text-[6px] text-gray-400">📍 {p.area}</p>
          </div>
        </div>
      ))}
    </div>
  </>
);

/* ── 2. Property + direct contact ─────────────────────────────── */
const PropertyScreen = () => (
  <>
    <StatusBar title="Property" />
    <div className="h-16 bg-gradient-to-br from-rose-200 to-rose-100 relative">
      <span className="absolute top-1 left-1 bg-amber-400 text-white text-[5px] font-bold px-1 py-0.5 rounded">
        ⚡ INSTANT BOOK
      </span>
    </div>
    <div className="p-2 space-y-1.5">
      <p className="text-[9px] font-bold text-gray-900 leading-tight">2 BHK in Indira Nagar</p>
      <div className="flex items-baseline gap-1">
        <span className="text-[11px] font-bold text-rose-500">₹14,000</span>
        <span className="text-[6px] text-gray-400">/month</span>
      </div>
      <div className="flex gap-1 text-[6px] text-gray-500">
        <span>🛏 2 Bed</span><span>🛁 2 Bath</span><span>⬚ 1200 sqft</span>
      </div>
      <div className="rounded-lg border border-gray-100 bg-white p-1.5">
        <p className="text-[6px] text-gray-400 mb-1">Listed by</p>
        <div className="flex items-center gap-1">
          <span className="w-4 h-4 rounded-full bg-rose-500 text-white text-[5px] font-bold flex items-center justify-center">
            TR
          </span>
          <span className="text-[7px] font-semibold text-gray-900">tolet for rent</span>
        </div>
        <div className="grid grid-cols-2 gap-1 mt-1.5">
          <span className="bg-rose-500 text-white text-[6px] font-semibold rounded py-1 text-center">
            Call Now
          </span>
          <span className="bg-[#25D366] text-white text-[6px] font-semibold rounded py-1 text-center">
            WhatsApp
          </span>
        </div>
      </div>
    </div>
  </>
);

/* ── 3. Message the owner ─────────────────────────────────────── */
const MessageScreen = () => (
  <>
    <StatusBar title="tolet for rent" />
    <div className="p-2 space-y-1.5">
      <div className="bg-white rounded-lg border border-gray-100 p-1.5 flex items-center gap-1.5">
        <span className="w-4 h-4 rounded bg-rose-100" />
        <span className="text-[6px] text-gray-500 leading-tight">2 BHK Indira Nagar · ₹14,000</span>
      </div>

      <div className="bg-rose-500 text-white text-[6px] rounded-lg rounded-br-sm px-2 py-1.5 ml-6 leading-snug">
        Hi, is this still available for 15 Sep?
      </div>
      <div className="bg-white border border-gray-100 text-gray-700 text-[6px] rounded-lg rounded-bl-sm px-2 py-1.5 mr-6 leading-snug">
        Yes! You can move in from the 15th.
      </div>
      <div className="bg-rose-500 text-white text-[6px] rounded-lg rounded-br-sm px-2 py-1.5 ml-6 leading-snug">
        Great — booking it now.
      </div>

      <div className="flex items-center gap-1 bg-white rounded-full border border-gray-200 px-2 py-1 mt-1">
        <span className="text-[6px] text-gray-300 flex-1">Message…</span>
        <span className="w-3.5 h-3.5 rounded-full bg-rose-500 flex items-center justify-center">
          <Icon name="Send" size={7} className="text-white" />
        </span>
      </div>
    </div>
  </>
);

/* ── 4. Booked ────────────────────────────────────────────────── */
const BookedScreen = () => (
  <>
    <StatusBar title="My Bookings" />
    <div className="p-2 space-y-2">
      <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2 text-center">
        <div className="w-6 h-6 rounded-full bg-emerald-500 mx-auto flex items-center justify-center">
          <Icon name="Check" size={10} className="text-white" />
        </div>
        <p className="text-[8px] font-bold text-emerald-800 mt-1">Booking confirmed</p>
        <p className="text-[6px] text-emerald-700">No brokerage charged</p>
      </div>
      <div className="bg-white rounded-lg border border-gray-100 p-1.5 space-y-1">
        {[
          ['Property', '2 BHK Indira Nagar'],
          ['Move-in', '15 Sep 2026'],
          ['Term', '11 months'],
          ['Total', '₹1,96,000'],
        ].map(([k, v]) => (
          <div key={k} className="flex items-center justify-between">
            <span className="text-[6px] text-gray-400">{k}</span>
            <span className="text-[6px] font-semibold text-gray-900">{v}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-1">
        {['Call', 'Chat', 'Cancel'].map((a) => (
          <span
            key={a}
            className="text-[6px] text-gray-600 bg-white border border-gray-200 rounded py-1 text-center"
          >
            {a}
          </span>
        ))}
      </div>
    </div>
  </>
);

const STEP_MS = 2800;

const SCREENS = [
  { key: 'splash',   label: 'Open the app',               Screen: SplashScreen },
  { key: 'search',   label: 'Browse verified properties', Screen: SearchScreen },
  { key: 'property', label: 'View a property',            Screen: PropertyScreen },
  { key: 'message',  label: 'Message the owner',          Screen: MessageScreen },
  { key: 'booked',   label: 'Booking confirmed',          Screen: BookedScreen },
];

const AppDownloadSection = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Someone who asked the OS to reduce motion shouldn't get a looping
    // animation; they see the first screen, static.
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduced) return;

    const id = setInterval(() => setStep((s) => (s + 1) % SCREENS.length), STEP_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <section className="py-12 sm:py-16 bg-rose-500 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-14">
          {/* Content */}
          <div className="flex-1 text-center lg:text-left">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
              Get the ToLetForRent App
            </h2>
            <p className="text-rose-100 mb-7 max-w-lg mx-auto lg:mx-0">
              The whole journey in your pocket — search verified listings, talk to owners
              directly, book instantly and track it all. Zero brokerage, always.
            </p>

            <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
              <a
                href="https://play.google.com/store/apps/details?id=com.toletforrent.app"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:opacity-90 transition"
              >
                <img
                  src="/assets/images/google-play-badge.svg"
                  alt="Get it on Google Play"
                  width="162"
                  height="48"
                  loading="lazy"
                  className="h-12"
                />
              </a>
            </div>
          </div>

          {/* Three phones showing consecutive steps of the same journey. They
              advance together, so the trio always reads left-to-right as
              "what you just did → what you're doing → what happens next". */}
          <div className="shrink-0 flex flex-col items-center gap-5">
            <div className="flex items-center justify-center">
              {[0, 1, 2].map((offset) => {
                const { Screen, key } = SCREENS[(step + offset) % SCREENS.length];
                const isCentre = offset === 1;
                return (
                  <PhoneFrame
                    key={offset}
                    className={
                      isCentre
                        ? 'w-[170px] z-10 relative'
                        : `hidden sm:block w-[136px] opacity-90 ${
                            offset === 0
                              ? 'rotate-[-6deg] translate-x-3'
                              : 'rotate-[6deg] -translate-x-3'
                          }`
                    }
                  >
                    {/* Keyed on the screen name so React remounts this subtree
                        each time the slot advances, replaying the fade. */}
                    <div key={key} aria-hidden="true" className="absolute inset-0 animate-fade-in">
                      <Screen />
                    </div>
                  </PhoneFrame>
                );
              })}
            </div>

            <div className="flex items-center gap-2">
              {SCREENS.map((sc, i) => (
                <button
                  key={sc.key}
                  type="button"
                  onClick={() => setStep(i)}
                  aria-label={sc.label}
                  className={`h-1.5 rounded-full transition-all ${
                    i === step ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AppDownloadSection;
